/*
  olvr-engraving-bridge.js
  ------------------------------------------------------------------
  Carries Globo Product Options values (engraving text + font) across
  the Nivoda Ring Studio handoff so they land on the ring's cart line.

  WHY THIS IS NEEDED
  Ring settings (tag `nivoda-setting`) do NOT add to cart via a form.
  Their "Add to cart" navigates to Nivoda Ring Studio
  (snippets/buy-buttons.liquid #add-diamond-link), which later creates a
  "Completed Ring" bundle product and adds THAT to the cart with its own
  line-item properties (Setting, Setting Variant, Diamond, Ring Size, and
  a `_detail_page_url` containing settingId).

  Globo never renders static `properties[...]` inputs — it builds line
  item properties only inside its OWN add-to-cart button (.gpo-product-atc).
  The ring flow bypasses that button, so the engraving/font are lost.

  HOW IT WORKS (Option B — persist & re-apply)
  1. CAPTURE: on a setting page, read Globo's engraving text widget and the
     selected font swatch directly from the DOM, keyed by the setting's
     numeric id (parsed from the #add-diamond-link href). Persist to
     localStorage.
  2. RE-APPLY: on the cart, find the Completed Ring bundle line whose
     `_detail_page_url` carries the same settingId, then merge the captured
     values into that line's properties via /cart/change.js — add-only,
     preserving Nivoda's existing `_`-prefixed bundle links.

  GLOBO FIELD MAP (from the product's embedded option config):
    - text-1  : text input, maxlength 16, placeholder "e.g. M&S, ..."
                -> cart label "Engraving"
    - radio-2 : swatch group, container class "engraving-fonts",
                values Classic/Modern/Script/Bold
                -> cart label "Engraving Font"

  Debug: set `localStorage.olvr_engraving_debug = '1'` to log + auto-dump
  the engraving DOM on setting pages.
*/
(function () {
  'use strict';

  var STORE_KEY = 'olvr_pending_engraving';
  var PROP_TEXT = 'Engraving';
  var PROP_FONT = 'Engraving Font';

  var DEBUG = false;
  try { DEBUG = localStorage.getItem('olvr_engraving_debug') === '1'; } catch (e) {}
  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ['[olvr-engraving]'].concat([].slice.call(arguments)));
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }
  function readQueue() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || []; } catch (e) { return []; }
  }
  function writeQueue(q) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(q)); } catch (e) { log('write fail', e); }
  }
  function settingIdFromUrl(url) {
    var m = /[?&]settingId=(\d+)/.exec(url || '');
    return m ? m[1] : null;
  }

  /* =================================================================
     CAPTURE  (setting product pages)
     ================================================================= */

  // Read Globo's engraving text widget. Targeted by the option's known
  // shape (maxlength 16 + distinctive placeholder), with looser fallbacks.
  function readEngravingText() {
    var sel = [
      'input[maxlength="16"][placeholder*="M&S"]',
      '.engraving-fonts input[type="text"]',
      '[class*="gpo"] input[maxlength="16"]',
      'input[maxlength="16"]'
    ];
    for (var i = 0; i < sel.length; i++) {
      var el = document.querySelector(sel[i]);
      if (el && (el.value || '').trim()) return el.value.trim();
    }
    return '';
  }

  // Read the selected font. Globo writes the chosen swatch's label into a
  // `.gpo-selected-values.has-value` div inside the font element (the one
  // carrying the `engraving-fonts` class), e.g. "Modern".
  function readFont() {
    var el = document.querySelector('.gpo-element.engraving-fonts .gpo-selected-values.has-value')
          || document.querySelector('.engraving-fonts .gpo-selected-values.has-value');
    var val = el ? el.textContent.trim() : '';
    if (val) return val;
    // Fallbacks: a real checked radio, or a swatch flagged `selected`.
    var group = document.querySelector('.engraving-fonts');
    if (group) {
      var radio = group.querySelector('input[type="radio"]:checked');
      if (radio && radio.value) return radio.value.trim();
      var picked = group.querySelector('.gpo-option-image.selected, .gpo-swatch.selected, [class*="selected"]');
      if (picked) {
        var t = (picked.getAttribute('data-value') || picked.title || picked.textContent || '').trim();
        var known = ['Classic', 'Modern', 'Script', 'Bold'];
        for (var i = 0; i < known.length; i++) { if (t.indexOf(known[i]) !== -1) return known[i]; }
        return t;
      }
    }
    return '';
  }

  // Selected ring size (used only as a tiebreaker when several rings share
  // the same setting + variant). Best-effort; degrades to '' harmlessly.
  function readRingSize() {
    var el = document.querySelector('.finger-sizes .gpo-selected-values.has-value')
          || document.querySelector('[class*="finger-size"] .gpo-selected-values.has-value');
    return el ? el.textContent.trim() : '';
  }

  function readGloboSelections() {
    var props = {};
    var t = readEngravingText();
    var f = readFont();
    if (t) props[PROP_TEXT] = t;
    if (f) props[PROP_FONT] = f;
    return props;
  }

  function stashCapture() {
    var link = document.getElementById('add-diamond-link');
    if (!link) return;

    var settingId = settingIdFromUrl(link.getAttribute('href')) ||
                    settingIdFromUrl(location.search);
    var props = readGloboSelections();
    var titleEl = document.querySelector('.product__title, .product-title, h1.title');
    var title = titleEl ? titleEl.textContent.trim() : '';

    // Replace any earlier capture for the same setting.
    var queue = readQueue().filter(function (e) {
      return e.settingId !== settingId && e.title !== title;
    });

    if (Object.keys(props).length && (settingId || title)) {
      var ringSize = readRingSize();
      queue.push({ settingId: settingId, title: title, ringSize: ringSize, props: props });
      log('captured', { settingId: settingId, title: title, ringSize: ringSize, props: props });
    } else {
      log('nothing to capture', { settingId: settingId, title: title, props: props });
    }
    writeQueue(queue);
  }

  // Diagnostic: dump candidate engraving/font DOM so selectors can be tuned.
  function dumpGloboDOM() {
    if (!DEBUG) return;
    var texts = [].slice.call(document.querySelectorAll('input[type="text"],textarea'))
      .map(function (e) { return { ml: e.maxLength, ph: e.placeholder, name: e.name, cls: e.className, val: e.value }; });
    var group = document.querySelector('.engraving-fonts');
    var fonts = group ? [].slice.call(group.querySelectorAll('*'))
      .filter(function (e) { return /selected|swatch|option/.test(e.className); })
      .map(function (e) { return { tag: e.tagName, cls: e.className, txt: (e.textContent || '').trim().slice(0, 24), title: e.title, val: e.value }; })
      .slice(0, 20) : 'NO .engraving-fonts FOUND';
    log('DOM DUMP — text inputs:', texts);
    log('DOM DUMP — .engraving-fonts:', fonts);
    log('DOM DUMP — readGloboSelections():', readGloboSelections());
  }

  function initCapture() {
    var link = document.getElementById('add-diamond-link');
    if (!link) return;                                   // not a setting page

    link.addEventListener('click', stashCapture, true);  // before navigation
    var container = document.querySelector('.product__info-container') || document;
    container.addEventListener('change', stashCapture, true);
    container.addEventListener('input', debounce(stashCapture, 400), true);

    // Globo renders client-side; dump after it has had time to mount, and
    // again on DOM mutations, so the debug dump reflects the real widgets.
    if (DEBUG) {
      setTimeout(dumpGloboDOM, 1500);
      setTimeout(dumpGloboDOM, 4000);
    }
  }

  /* =================================================================
     RE-APPLY  (cart contexts)
     ================================================================= */

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // Match a Nivoda bundle line to a captured entry by settingId (preferred,
  // parsed from _detail_page_url) or by Setting title (fallback), and only
  // if the line is missing any captured value.
  function lineNeeds(entry, line) {
    var p = line.properties || {};
    var matches = false;
    if (entry.settingId && p._detail_page_url) {
      matches = settingIdFromUrl(p._detail_page_url) === entry.settingId;
    }
    if (!matches && entry.title && p.Setting) {
      matches = p.Setting === entry.title;
    }
    if (!matches) return false;
    return Object.keys(entry.props).some(function (k) { return p[k] !== entry.props[k]; });
  }

  function applyPending() {
    var queue = readQueue();
    if (!queue.length) return Promise.resolve(false);

    return getCart().then(function (cart) {
      var items = cart.items || [];
      var ops = [], consumed = [];

      queue.forEach(function (entry) {
        // All bundle lines matching this capture (same setting, missing our props).
        var candidates = [];
        for (var i = 0; i < items.length; i++) { if (lineNeeds(entry, items[i])) candidates.push(i); }
        if (!candidates.length) return;

        // Default to the newest matching line; if we captured a ring size,
        // prefer the line whose Ring Size matches (disambiguates same-setting
        // rings). Numeric compare so "3" and "3.00" still match.
        var idx = candidates[candidates.length - 1];
        if (entry.ringSize) {
          for (var c = 0; c < candidates.length; c++) {
            var rs = (items[candidates[c]].properties || {})['Ring Size'];
            if (rs && parseFloat(rs) === parseFloat(entry.ringSize)) { idx = candidates[c]; break; }
          }
        }
        var merged = Object.assign({}, items[idx].properties || {}, entry.props); // add-only
        ops.push({ line: idx + 1, properties: merged });
        consumed.push(entry);
        log('will patch line ' + (idx + 1), merged);
      });

      if (!ops.length) return false;

      var chain = Promise.resolve();
      ops.forEach(function (op) {
        chain = chain.then(function () {
          return fetch(root() + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: op.line, properties: op.properties })
          }).then(function (r) {
            if (!r.ok) throw new Error('change.js ' + r.status);
            log('patched line ' + op.line);
          });
        });
      });

      return chain.then(function () {
        var remaining = readQueue().filter(function (e) {
          return !consumed.some(function (c) {
            return (c.settingId && c.settingId === e.settingId) ||
                   (c.title && c.title === e.title);
          });
        });
        writeQueue(remaining);
        return true;
      });
    }).catch(function (e) { log('applyPending error', e); return false; });
  }

  function refreshCartUI() {
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    if (window.PUB_SUB_EVENTS && window.publish) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'olvr-engraving-bridge' });
    }
  }

  function initApply() {
    var attempts = 0;
    (function tick() {
      attempts++;
      applyPending().then(function (changed) {
        if (changed) { refreshCartUI(); return; }
        if (attempts < 5 && readQueue().length) setTimeout(tick, 1200);
      });
    })();
  }

  /* ---- util + boot ------------------------------------------------- */
  function debounce(fn, ms) {
    var t; return function () { var c = this, a = arguments; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }
  function boot() { initCapture(); initApply(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
